import React, { FC } from 'react'
import { Dialog, Div, Flex } from '@sendoutcards/quantum-design-ui'
import { Intro } from '../components/Intro/Intro'
import { UpsaleSuccess } from '../components/UpsaleSuccess'
import { UpsaleCheckout } from '../components/checkout/UpsaleCheckout'
import { useSelector, useState } from 'src/hooks'
import { PricingPage } from '../components/PricingPage'
import { UpsaleTopBar } from '../components/shared/UpsaleTopbar'
import { Portal } from 'src/portal/portal'
import { PlanFragment } from 'src/legacy_graphql'

export type UpsaleSteps = 'Intro' | 'Selecting' | 'Form' | 'Success'

type CardUpsaleProps = {
  initialStep: UpsaleSteps
  selectedPlanId?: string
  shouldShowCloseIcon?: boolean
  onClose?: () => void
}

export const AppUpsale: FC<CardUpsaleProps> = ({
  initialStep,
  selectedPlanId,
  onClose,
  shouldShowCloseIcon = true,
}) => {
  const [currentStep, setCurrentStep] = useState<UpsaleSteps>(initialStep)
  const [isDialogOpen, setIsDialogOpen] = useState(true)
  const [selPlan, setSelPlan] = useState<PlanFragment | undefined>(undefined)
  const [upsaleSuccessMsg, setUpsaleSuccessMsg] = useState(
    'Way to go! You have successfully upgraded to',
  )

  const onBackStep = (step: UpsaleSteps) => {
    switch (step) {
      case 'Selecting':
        setCurrentStep('Intro')
        break
      case 'Form':
        setCurrentStep('Selecting')
        break
      default:
        break
    }
  }

  const onNext = (step: UpsaleSteps) => {
    setCurrentStep(step)
  }

  const onPlanSelect = (plan: PlanFragment, isKeepButton?: boolean) => {
    setSelPlan(plan)
    setCurrentStep('Form')
  }

  const step = {
    Intro: (
      <Intro
        title="You’re Making a Difference!"
        description={
          'Keep it going! Check out our subscriptions for incredible options to continue your daily heartfelt card sending'
        }
        onNext={onNext}
      />
    ),
    Selecting: (
      <PricingPage
        pricingTile={{
          title: 'Change Even More Lives When You Subscribe and Save!',
          description:
            "You’re officially halfway through the 10 Card Challenge and changing lives in a positive way. That's why we are offering you these Heartfelt subscriptions based on your card-sending activity. Go with the most popular to get the best savings - we know you’ll love it!",
          onSelect: onPlanSelect,
        }}
        comparisonTable={{
          title: 'Not sure yet? Here’s a side-by-side comparison table',
        }}
      />
    ),
    Form: (
      <>
        {selPlan && (
          <UpsaleCheckout
            title="Amazing! This is a Great Choice!"
            description="Review the subscription information below and confirm your purchase."
            selectedItem={selPlan}
            actionTitle="Add To Account"
            onPurchaseSuccess={successMsg => {
              setUpsaleSuccessMsg(successMsg)
              setCurrentStep('Success')
            }}
            onCancel={() => {
              setSelPlan(undefined)
              setCurrentStep('Selecting')
            }}
          />
        )}
      </>
    ),
    Success: (
      <>
        {selPlan && (
          <UpsaleSuccess
            title={upsaleSuccessMsg}
            productTitle={selPlan.title}
            icon="smile_card_w_hearts"
            onClose={() => setIsDialogOpen(false)}
          />
        )}
      </>
    ),
  }[currentStep]

  const isDialogBreakpoint = useSelector(state => state.window.width < 649)

  const dialogWidthContraints = {
    Intro: { maxWidth: '630px', width: isDialogBreakpoint ? '100%' : '95%' },
    Selecting: { maxWidth: '1000px', width: '' },
    Form: { maxWidth: '1000px', width: '' },
    Success: { maxWidth: '630px', width: isDialogBreakpoint ? '100%' : '95%' },
  }[currentStep]

  return (
    <Portal>
      <Div data-upsale="upsale-dialog">
        <Dialog
          css={{ padding: '16px' }}
          maxHeight={isDialogBreakpoint ? 'undefined' : '870px'}
          isOpen={isDialogOpen}
          insetOverride="x0"
          borderRadius="large"
          pinned={isDialogBreakpoint ? 'bottom' : undefined}
          zIndex={3000}
          height={isDialogBreakpoint ? '95%' : undefined}
          {...dialogWidthContraints}
        >
          {shouldShowCloseIcon && (
            <UpsaleTopBar
              handlePrevStep={
                currentStep === 'Selecting' || currentStep === 'Form'
                  ? () => onBackStep(currentStep)
                  : undefined
              }
              onClose={() => {
                setIsDialogOpen(false)
                onClose?.()
              }}
            />
          )}
          <Flex
            minHeight="520px"
            position="relative"
            overflow="auto"
            style={{ borderRadius: '0 0 28px 28px' }}
          >
            <Flex minHeight="520px" maxHeight="1100px" width="100%">
              {step}
            </Flex>
          </Flex>
        </Dialog>
      </Div>
    </Portal>
  )
}

export default AppUpsale
